import { ArrowDownIcon } from '@blueprinthq/joy'
import {
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text
} from '@chakra-ui/react'
import { Mic, PersonDictate, Upload, VideoCamera } from '@components/icons'
import { useExperienceManager } from '@hooks'
import { useStoreState } from 'easy-peasy'
import React from 'react'
import { useHistory } from 'react-router-dom'
import { useOrganizationControllerGetSessionCredit } from '~/clinician-api'
import {
  OutOfSessionsModal,
  useOutOfSessionsModal
} from '../../../components/out-of-sessions-modal'

interface StartNewSessionMenuProps {
  id?: string
  onInPersonClick?: () => void
  onDictationClick?: () => void
  onUploadClick?: () => void
  onTelehealthClick?: () => void
}

export const StartNewSessionMenu: React.FC<StartNewSessionMenuProps> = ({
  id,
  onInPersonClick,
  onDictationClick,
  onUploadClick,
  onTelehealthClick
}) => {
  const { isTelehealthEnabled, usageBasedPricingEnabled } =
    useExperienceManager()
  const { isOpen, openModal, closeModal } = useOutOfSessionsModal()

  // Get user to access organization ID
  const { user } = useStoreState((state: any) => state.auth)

  // Fetch session credits directly
  const { data: sessionCredits } = useOrganizationControllerGetSessionCredit(
    user?.clinic?.organization_id || '',
    {
      query: {
        enabled: !!(usageBasedPricingEnabled && user?.clinic?.organization_id)
      }
    }
  )

  const handleUsageClick = (fn?: () => any) => {
    if (usageBasedPricingEnabled) {
      if (
        !sessionCredits ||
        (sessionCredits.creditsRemaining === 0 &&
          sessionCredits.freeCreditsRemaining === 0)
      ) {
        openModal()
      } else {
        fn?.()
      }
    } else {
      fn?.()
    }
  }

  return (
    <>
      <Menu>
        <MenuButton
          as={Button}
          _focus={{
            outline: 'none'
          }}
          rightIcon={<ArrowDownIcon fill="white" />}
        >
          <Text pl="xsmall">New Session</Text>
        </MenuButton>
        <Portal>
          <MenuList>
            {isTelehealthEnabled && (
              <MenuItem
                id={id + '-telehealth'}
                onClick={() => handleUsageClick(onTelehealthClick)}
              >
                <HStack w="100%">
                  <VideoCamera width="16px" />
                  <Text>Start video session</Text>
                </HStack>
              </MenuItem>
            )}
            <MenuItem
              id={id + '-audio'}
              onClick={() => handleUsageClick(onInPersonClick)}
            >
              <HStack w="100%">
                <Mic width="16px" />
                <Text>Record session audio</Text>
              </HStack>
            </MenuItem>
            <MenuItem
              id={id + '-dictation'}
              onClick={() => handleUsageClick(onDictationClick)}
            >
              <HStack w="100%">
                <PersonDictate width="16px" fill="black" />
                <Text>Dictate a note</Text>
              </HStack>
            </MenuItem>
            <MenuItem
              id={id + '-upload'}
              onClick={() => handleUsageClick(onUploadClick)}
            >
              <HStack w="100%">
                <Upload width="16px" fill="black" />
                <Text>Upload recording</Text>
              </HStack>
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
      <OutOfSessionsModal isOpen={isOpen} onClose={closeModal} />
    </>
  )
}
